import { useCallback } from "react";
import toast from "react-hot-toast";
import type { TypedMutationTrigger } from "@reduxjs/toolkit/query/react";
import { useTranslation } from "react-i18next";

interface MinimalMutationResult {
  isLoading: boolean;
  isSuccess: boolean;
}

interface ErrorMessages {
  /** The default error message to show if no code matches */
  default: string;
  /** Specific error messages mapped by error code (e.g., "readonly_quantity") */
  [errorCode: string]: string;
}

interface UseMutationWithFeedbackProps<T, U> {
  mutation: [TypedMutationTrigger<T, U, any>, MinimalMutationResult];
  loadingMessage?: string;
  successMessage?: string | null;
  errorMessages?: ErrorMessages;
  overrideApiErrorMessage?: boolean;
  /** Define the type of toast to show on success. Defaults to 'success'. */
  successToastType?: "success" | "info";
}

/**
 * A hook that wraps an RTK Query mutation to automatically display
 * loading, success, and error toasts using react-hot-toast. It provides more control
 * than toast.promise by handling the toast lifecycle manually.
 */
export const useMutationWithFeedback = <T, U>({
  mutation: [originalTrigger, mutationResult],
  loadingMessage,
  successMessage,
  errorMessages,
  overrideApiErrorMessage = true, // Default to true
  successToastType = "success", // Default to 'success'
}: UseMutationWithFeedbackProps<T, U>) => {
  const { t } = useTranslation();

  // Set default messages from translation files if not provided in props
  const finalLoadingMessage =
    loadingMessage ?? t("feedback.default.loading", "Processing...");

  // If no errorMessages object is provided, create a default one using the generic translation
  const finalErrorMessages: ErrorMessages = errorMessages ?? {
    default: t(
      "feedback.default.error",
      "An error occurred. Please try again."
    ),
  };

  const trigger = useCallback(
    async (arg: U) => {
      // The toastId will only be set if a loading toast is created.
      let toastId: string | undefined;

      // --- Only show a loading toast if a success message is expected. ---
      if (successMessage) {
        toastId = toast.loading(finalLoadingMessage);
      }

      try {
        const result = await originalTrigger(arg).unwrap();

        // On success, update the toast only if it was created.
        if (toastId && successMessage) {
          // Use the new prop to decide which toast function to call
          if (successToastType === "info") {
            toast(successMessage, { id: toastId }); // 'blank' type, which we style as 'info'
          } else {
            toast.success(successMessage, { id: toastId }); // Default behavior
          }
        }

        return { success: true, data: result as T };
      } catch (err: any) {
        // Determine which message to show
        let messageToShow = finalErrorMessages.default;

        const topLevelCode = err?.data?.code;
        let effectiveCode = topLevelCode;

        // --- ENHANCEMENT: Dig for nested WP REST validation codes ---
        // If the error is a generic parameter error, look inside 'data.details'
        // API Structure: { code: 'rest_invalid_param', data: { details: { field_name: { code: 'specific_error', ... } } } }
        if (topLevelCode === "rest_invalid_param" && err?.data?.data?.details) {
          const details = err.data.data.details;
          const firstField = Object.keys(details)[0];
          if (firstField && details[firstField]?.code) {
            effectiveCode = details[firstField].code;
          }
        }

        // 1. Check for a specific mapped error code (using the specific effective code)
        if (effectiveCode && finalErrorMessages[effectiveCode]) {
          messageToShow = finalErrorMessages[effectiveCode];
        }
        // 2. Fallback: If no specific code found, try the top-level code (if different)
        else if (
          topLevelCode &&
          topLevelCode !== effectiveCode &&
          finalErrorMessages[topLevelCode]
        ) {
          messageToShow = finalErrorMessages[topLevelCode];
        }
        // 3. If no specific code, and we are NOT overriding API messages, try to use the API message
        else if (!overrideApiErrorMessage && err?.data?.message) {
          messageToShow = err.data.message;
        }
        // 4. Fallback to default (already set)

        toast.error(messageToShow, { id: toastId });

        console.error("Mutation failed:", err);
        return { success: false, error: err };
      }
    },
    [
      originalTrigger,
      finalLoadingMessage,
      successMessage,
      finalErrorMessages,
      overrideApiErrorMessage,
      successToastType,
    ]
  );

  return {
    trigger,
    isLoading: mutationResult.isLoading,
    isSuccess: mutationResult.isSuccess,
  };
};
